import { canonicalizeFaxNumberInput } from '@/shared/lib/formatters';

export const DEFAULT_DOCUMENT_TITLE = 'Untitled';

export interface DocumentRecipient {
  name: string;
  organization: string;
  faxNumber: string;
}

/**
 * Inputs used to create a new document's editable Markdown header.
 */
export interface DocumentHeaderTemplateData {
  title: string;
  prescriberName: string;
  date: string;
  patientName: string;
  patientDateOfBirth: string;
  patientMrn: string;
  recipient: DocumentRecipient;
}

function headerValue(value: string): string {
  return value.replace(/[\r\n]+/g, ' ').trim();
}

function headerLine(label: string, value: string): string {
  const normalized = headerValue(value);
  return normalized === '' ? `**${label}:**` : `**${label}:** ${normalized}`;
}

const recipientFieldPatterns = [
  /^(?:\*\*)?(To|Organization|Fax):\*\*(.*)$/i,
  /^\*\*(To|Organization|Fax):(.*)\*\*$/i,
  /^(To|Organization|Fax):(.*)$/i,
];

/** A `# title` heading followed by one bold `Label: value` line per field. */
export function buildHeaderMarkdown(
  title: string,
  fields: ReadonlyArray<readonly [label: string, value: string]>
): string {
  return [
    `# ${headerValue(title) || DEFAULT_DOCUMENT_TITLE}`,
    '',
    ...fields.map(([label, value]) => headerLine(label, value)),
  ].join('\n');
}

/**
 * The document's `#` heading. Returns `fallback` (empty by default) when there
 * is none, so sent and previewed PDFs carry no title rather than a made-up one;
 * pass a fallback only for on-screen labels.
 */
export function documentTitleFromMarkdown(markdown: string, fallback = ''): string {
  const heading = markdown.match(/^\s*#\s+(.+?)\s*$/m)?.[1]?.trim();
  return heading || fallback;
}

/** Return the message body without the Markdown heading used as its subject. */
export function documentBodyFromMarkdown(markdown: string): string {
  return markdown.replace(/^\s*#\s+.*(?:\n|$)/, '').trim();
}

function headerField(markdown: string, label: string): string {
  for (const line of markdown.split('\n')) {
    for (const pattern of recipientFieldPatterns) {
      const match = pattern.exec(line.trim());
      if (match?.[1]?.toLowerCase() === label.toLowerCase()) {
        return match[2]?.trim() ?? '';
      }
    }
  }

  return '';
}

/**
 * Set the value of existing `**Label:**` header lines, e.g. after a recipient
 * is chosen. Labels the document doesn't have are left alone.
 */
export function withDocumentHeaderFields(
  markdown: string,
  fields: Readonly<Partial<Record<'To' | 'Organization' | 'Fax', string>>>
): string {
  const remaining = new Map(Object.entries(fields));
  return markdown
    .split('\n')
    .map((line) => {
      for (const pattern of recipientFieldPatterns) {
        const label = pattern.exec(line.trim())?.[1];
        const key = [...remaining.keys()].find(
          (candidate) => candidate.toLowerCase() === label?.toLowerCase()
        );
        if (key === undefined) {
          continue;
        }
        const value = remaining.get(key) ?? '';
        remaining.delete(key);
        return headerLine(key, value);
      }
      return line;
    })
    .join('\n');
}

export function documentRecipientFromMarkdown(markdown: string): DocumentRecipient {
  return {
    name: headerField(markdown, 'To'),
    organization: headerField(markdown, 'Organization'),
    faxNumber: canonicalizeFaxNumberInput(headerField(markdown, 'Fax')),
  };
}
